<template>
    <div class="offer-item-tile" :class="{ 'not-available': !isAvailable }" @click="onClickItem">
        <div v-if="articleInCartQuantity > 0" class="quantity-badge">{{ articleInCartQuantity }}x</div>

        <span class="item-name">{{ getName(item, true) }} </span>
        <i v-if="!isAvailable" class="fas fa-ban"></i>
        <div class="item-color"></div>

        <div v-if="canBeAddedWithoutUserSelection" class="open-popover-btn" @click.stop="onOpenPopoverClick">
            &plus;
        </div>
    </div>
</template>

<script setup lang="ts">
    import type { ICustomerArticleWithOptions } from "@/data/model/Customer";
    import { useCartStore } from "@/data/stores/cart";
    import { getName } from "@/shared/helpers/ItemNameHelper";
    import { computed } from "vue";

    const cart = useCartStore();

    // define properties
    const props = defineProps<{
        item: ICustomerArticleWithOptions;
        isAvailable: boolean;
    }>();

    const emit = defineEmits<{
        addToCartPopoverClick: [item: ICustomerArticleWithOptions];
        quickAddClick: [item: ICustomerArticleWithOptions];
    }>();

    const articleInCartQuantity = computed(() => cart.getCartItemQuantity(props.item.articleId));
    const maxQuantity = computed(() => {
        const max = props.item.maxQuantity ?? Number.MAX_VALUE;
        return Math.max(0, max - articleInCartQuantity.value);
    });

    const isOpenPriceValid = computed(() => !props.item.isOpenPriceEnabled);

    const canBeAddedWithoutUserSelection = computed(
        () => isOpenPriceValid.value && maxQuantity.value > 0 && props.item.canBeAddedWithoutUserSelection
    );

    function onClickItem() {
        if (canBeAddedWithoutUserSelection.value) {
            emit("quickAddClick", props.item);
        } else {
            emit("addToCartPopoverClick", props.item);
        }
    }

    function onOpenPopoverClick() {
        emit("addToCartPopoverClick", props.item);
    }
</script>

<style scoped lang="scss">
    @use "./../../../assets/styles/scss/shared/_colors.scss";
    @use "./../../../assets/styles/scss/shared/_mixins.scss";

    .offer-item-tile {
        position: relative;
        display: flex;
        align-items: center;
        justify-content: center;
        min-height: 100px;
        padding: 0.25em;

        background: white;
        border: 1px solid #dcdcdc;
        border-radius: 0.25em;

        color: colors.$fontColor;
        font-size: 18px;
        cursor: pointer;

        transition: 0.2s linear;

        @include mixins.hover-and-active() {
            color: colors.$fontColorHover;
            background-color: #ddd;
            border-color: #bbb;
        }

        > .item-name {
            font-weight: 600;
            color: colors.$fontColor;
            text-align: center;
            word-break: break-word;
        }

        .item-color {
            position: absolute;
            bottom: 0;
            height: 5px;
            left: 0;
            right: 0;
            // background-color: colors.$orange;
            background-color: #777;
            border-bottom-left-radius: 4px;
            border-bottom-right-radius: 4px;
        }

        &.not-available {
            cursor: default;
            background-color: #f7f7f7 !important;
            border-color: #dcdcdc !important;

            .item-name {
                color: #bbb;
            }

            i {
                position: absolute;
                top: 0.5em;
                right: 0.5em;
                color: #ccc !important;
            }

            .item-color {
                display: none;
            }
        }

        .quantity-badge {
            position: absolute;
            top: 0.25em;
            left: 0.5em;
            font-size: 18px;
            font-weight: 600;
            color: var(--colorPrimary);
        }

        .open-popover-btn {
            position: absolute;
            bottom: 5px;
            right: -1px;
            width: 50px;
            height: 35px;
            box-sizing: border-box;
            border: 1px solid #dedede;
            border-top-left-radius: 5px;
            border-top-right-radius: 5px;
            background-color: rgba(255, 255, 255, 0.5);
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 24px;
            font-weight: 600;
            transition: 0.2s linear;

            @include mixins.hover-and-active() {
                background-color: rgba(255, 255, 255, 1);
            }
        }

        @include mixins.hover-and-active() {
            > .open-popover-btn {
                border-color: colors.$light-gray-500;
            }
        }
    }
</style>
